import { useEffect, useMemo, useRef } from "react"
import { boundsIntersect } from "@nova/calculate-geospatial"
import { AirspaceRestrictionCategory } from "@nova/constants"
import { useDebouncedViewport } from "features/map/GlobalViewState"
import { useLocalStorageValue } from "tools/store/localStorageRegistry"
import { getMinShapeArea, getPaddedViewportBounds, isAreaVisible } from "../../zoomLod"
import { useAirspaceLayer } from "../../useAirspaceLayer"
import { RESTRICTION_LAYER_ID } from "../../layerIds"
import { fetchAndCacheRestrictions, useRestrictionsCache } from "./restrictionCache"

const VIEWPORT_UPDATE_DEBOUNCE_MS = 300

export function isRestrictionVisible(
    properties: { restrictionCategories?: AirspaceRestrictionCategory[] },
    restrictions: Record<AirspaceRestrictionCategory, boolean>
) {
    return (properties.restrictionCategories ?? []).some(
        (category) => restrictions[category] ?? true
    )
}

export const useRestrictionLayer = (enabled: boolean) => {
    const abortRef = useRef<AbortController | null>(null)
    const getNewAbortController = () => {
        abortRef.current?.abort()
        const abortController = new AbortController()
        abortRef.current = abortController
        return abortController
    }

    const airspaceFilters = useLocalStorageValue("airspaceFilters")
    const restrictionsCategoryVisible = airspaceFilters.categoriesVisible.restrictions
    const viewport = useDebouncedViewport(VIEWPORT_UPDATE_DEBOUNCE_MS)

    useEffect(() => {
        if (!restrictionsCategoryVisible) return
        const abortController = getNewAbortController()
        fetchAndCacheRestrictions({ abortController })
        return () => {
            abortRef.current?.abort()
            abortRef.current = null
        }
    }, [restrictionsCategoryVisible, viewport])

    const restrictions = useMemo(
        () => ({
            ...airspaceFilters.restrictions.usa,
            ...airspaceFilters.restrictions.canada,
        }),
        [airspaceFilters.restrictions.usa, airspaceFilters.restrictions.canada]
    )

    const cache = useRestrictionsCache()
    const minShapeArea = getMinShapeArea(viewport.zoom)
    const paddedViewportBounds = useMemo(() => getPaddedViewportBounds(viewport), [viewport])
    const visibleFeatures = useMemo(
        () =>
            restrictionsCategoryVisible
                ? [...cache.values()]
                      .filter((cached) => isRestrictionVisible(cached.properties, restrictions))
                      .filter((cached) => isAreaVisible(cached.area, minShapeArea))
                      .filter((cached) => boundsIntersect(cached.bbox, paddedViewportBounds))
                      .map((cached) => cached.feature)
                : [],
        [restrictionsCategoryVisible, cache, restrictions, minShapeArea, paddedViewportBounds]
    )

    return useAirspaceLayer({ type: RESTRICTION_LAYER_ID, visibleFeatures, enabled })
}
